import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authService, LoginCredentials } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { LANDING_MODULE_ORDER, resolveLandingPathForModuleKey } from '../config/permissionModules';

export interface User {
  id: number;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userDisplayName: string;
  userIsActive?: boolean;
  role?: 'ADMIN' | 'USER' | 'CLIENT' | 'GUEST';
  /** Office / vessel / supplier / manning-agent account classification (mirrors `User.typeLocation`). */
  typeLocation?: 'OFFICE' | 'VESSEL' | 'SUPPLIER' | 'MANNING_AGENT';
  /** When `typeLocation` is VESSEL, the vessel this login is scoped to (use for filtering lists). */
  vesselId?: number | null;
  /** When `typeLocation` is MANNING_AGENT, the manning agent this login is scoped to. */
  manningAgentId?: number | null;
  departmentId?: number | null;
  vessel?: { id: number; vesselName: string } | null;
  manningAgent?: { id: number; code?: string | null; name: string } | null;
  department?: { id: number; code?: string; name: string } | null;
}

/** Normalize `/profile` payload so vessel scope checks stay reliable (case, numeric id). */
function normalizeUserFromProfile(raw: User): User {
  const typeRaw = raw.typeLocation;
  const upper =
    typeof typeRaw === 'string' ? typeRaw.trim().toUpperCase() : typeRaw;
  const typeLocation: User['typeLocation'] =
    upper === 'OFFICE' || upper === 'VESSEL' || upper === 'SUPPLIER' || upper === 'MANNING_AGENT'
      ? upper
      : raw.typeLocation;

  let vesselId: number | null = null;
  if (raw.vesselId != null) {
    const n = Number(raw.vesselId);
    if (Number.isFinite(n) && n > 0) {
      vesselId = n;
    }
  }

  let manningAgentId: number | null = null;
  if (raw.manningAgentId != null) {
    const n = Number(raw.manningAgentId);
    if (Number.isFinite(n) && n > 0) {
      manningAgentId = n;
    }
  }

  let departmentId: number | null = null;
  if (raw.departmentId != null) {
    const n = Number(raw.departmentId);
    if (Number.isFinite(n) && n > 0) {
      departmentId = n;
    }
  }

  return {
    ...raw,
    typeLocation,
    vesselId,
    manningAgentId,
    departmentId,
  };
}

export type ModulePermissionAction = 'view' | 'edit' | 'delete' | 'close';

export interface ModulePermissionFlags {
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canClose?: boolean;
}

export type PermissionsMap = Record<string, ModulePermissionFlags>;

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  permissions: PermissionsMap;
  /** True after first successful fetch of permissions (so sidebar can safely filter by canView). */
  permissionsLoaded: boolean;
  can: (moduleKey: string, action: ModulePermissionAction) => boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const emptyPermissions: PermissionsMap = {};

function resolvePostLoginLandingPath(
  currentUser: User | null,
  currentPermissions: PermissionsMap,
): string {
  if (!currentUser) return '/dashboard';
  if (currentUser.role === 'ADMIN') return '/dashboard';
  const canView = (moduleKey: string) => Boolean(currentPermissions[moduleKey]?.canView);
  for (const moduleKey of LANDING_MODULE_ORDER) {
    if (!canView(moduleKey)) continue;
    const path = resolveLandingPathForModuleKey(moduleKey, currentUser.vesselId);
    if (path) return path;
  }
  return '/dashboard';
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<PermissionsMap>(emptyPermissions);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const navigate = useNavigate();

  const fetchUser = async (): Promise<void> => {
    try {
      const response = await api.get<User>('/profile');
      setUser(normalizeUserFromProfile(response.data));
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setUser(null);
    }
  };

  const fetchPermissions = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<PermissionsMap>('/permissions');
      setPermissions(response.data ?? emptyPermissions);
      setPermissionsLoaded(true);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions(emptyPermissions);
      setPermissionsLoaded(true);
    }
  }, []);

  const can = useCallback(
    (moduleKey: string, action: ModulePermissionAction): boolean => {
      if (!user) return false;
      if (user.role === 'ADMIN') return true;
      const p = permissions[moduleKey];
      if (!p) return false;
      switch (action) {
        case 'view':
          return p.canView;
        case 'edit':
          return p.canEdit;
        case 'delete':
          return p.canDelete;
        case 'close':
          return Boolean(p.canClose);
        default:
          return false;
      }
    },
    [user, permissions],
  );

  useEffect(() => {
    const checkAuth = async () => {
      const token = authService.getToken();
      if (token) {
        try {
          await authService.validateSession();
          setIsAuthenticated(true);
          await fetchUser();
          await fetchPermissions();
          // Validate and fix view preferences on session check (e.g., page refresh)
          try {
            await api.get('/settings/user/view-preferences');
          } catch (error) {
            console.error('Failed to fetch view preferences on session check:', error);
          }
        } catch (error) {
          setIsAuthenticated(false);
          setUser(null);
          setPermissions(emptyPermissions);
          setPermissionsLoaded(false);
          authService.setToken('');
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setPermissions(emptyPermissions);
        setPermissionsLoaded(false);
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await authService.login(credentials);
      authService.setToken(response.access_token);
      setIsAuthenticated(true);
      const profileResponse = await api.get<User>('/profile');
      const normalizedUser = normalizeUserFromProfile(profileResponse.data);
      setUser(normalizedUser);
      const permissionsResponse = await api.get<PermissionsMap>('/permissions');
      const resolvedPermissions = permissionsResponse.data ?? emptyPermissions;
      setPermissions(resolvedPermissions);
      setPermissionsLoaded(true);

      // Wait a moment for backend validation to complete, then fetch view preferences
      // The backend validates and sets defaults on login, so we fetch to ensure frontend has latest data
      await new Promise(resolve => setTimeout(resolve, 300)); // Small delay to ensure backend validation completes
      
      try {
        const viewPrefsResponse = await api.get('/settings/user/view-preferences');
        const viewPreferences = viewPrefsResponse.data.data || {};
        
        // Trigger events to refresh sidebar view preferences
        window.dispatchEvent(new CustomEvent('viewPreferencesUpdated'));
        window.dispatchEvent(new CustomEvent('userAuthenticated'));
      } catch (error) {
        console.error('Failed to fetch view preferences on login:', error);
      }
      
      const destination = resolvePostLoginLandingPath(normalizedUser, resolvedPermissions);
      navigate(destination);
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      setPermissions(emptyPermissions);
      setPermissionsLoaded(false);
      navigate('/login');
    }
  };

  const refreshUser = async () => {
    if (isAuthenticated) {
      await fetchUser();
      await fetchPermissions();
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, permissions, permissionsLoaded, can, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

